import type { TerrainType } from "../world";
import type { AnimationStateOverrides } from "./AnimationStateMachine";
import type { AnimalDescriptor, AnimalKind } from "./types";

export const AMPHIBIOUS_TRAVERSABLE: ReadonlySet<TerrainType> = new Set<TerrainType>([
  "water",
  "ice",
  "land",
]);
export const WATER_ONLY_TRAVERSABLE: ReadonlySet<TerrainType> = new Set<TerrainType>(["water"]);

// Penguins propel themselves horizontally in the water, so the body should
// pitch ~80° forward when swimming/diving. Walking and other states stay
// upright. This is a player-only visual override applied via the animation
// state machine; the underlying builder pose stays the same.
const PENGUIN_ANIMATION_OVERRIDES: AnimationStateOverrides = {
  swimming: { pitch: -1.4 },
  diving: { pitch: -1.4 },
  surfacing: { pitch: -0.6 },
  plunging: { pitch: -1.55 },
};

// Seals waddle on land — their body lifts subtly with each flipper stroke.
// The walking state gets a small bob (the builder leaves Y nearly flat).
const SEAL_ANIMATION_OVERRIDES: AnimationStateOverrides = {
  walking: { bobAmplitude: 0.05, bobRate: 3.5 },
  running: { bobAmplitude: 0.07, bobRate: 5.5 },
};

const SEAL: AnimalDescriptor = {
  kind: "seal",
  speeds: { walk: 2.4, swim: 5.4 },
  vertical: { maxDiveDepth: 12, diveDescentRate: 3.2, surfaceAscentRate: 2.2 },
  traversableTerrain: AMPHIBIOUS_TRAVERSABLE,
  animationOverrides: SEAL_ANIMATION_OVERRIDES,
};

const PENGUIN: AnimalDescriptor = {
  kind: "penguin",
  speeds: { walk: 3.6, swim: 4.6 },
  vertical: { maxDiveDepth: 9, diveDescentRate: 2.6, surfaceAscentRate: 2.6 },
  traversableTerrain: AMPHIBIOUS_TRAVERSABLE,
  animationOverrides: PENGUIN_ANIMATION_OVERRIDES,
};

const FISH: AnimalDescriptor = {
  kind: "fish",
  speeds: { walk: 0, swim: 6.0 },
  vertical: { maxDiveDepth: 14, diveDescentRate: 3.6, surfaceAscentRate: 1.8 },
  traversableTerrain: WATER_ONLY_TRAVERSABLE,
};

const DESCRIPTORS: Readonly<Record<AnimalKind, AnimalDescriptor>> = {
  seal: SEAL,
  penguin: PENGUIN,
  fish: FISH,
};

export const ANIMAL_KINDS: readonly AnimalKind[] = ["seal", "penguin", "fish"];

export function getAnimalDescriptor(kind: AnimalKind): AnimalDescriptor {
  return DESCRIPTORS[kind];
}

export function listAnimals(): readonly AnimalDescriptor[] {
  return ANIMAL_KINDS.map(getAnimalDescriptor);
}
